import { ISO_DATE_FORMAT } from '@fox/atoms/utils/date-functions';
import { tryParseJSONObject } from '@fox/atoms/utils/string';
import moment from 'moment';
import { useEffect } from 'react';
import { IAnswerValue } from '../../form-root/contracts';
import { useFormRuntimeContext } from '../../form-runtime-context/form-runtime-context';
import { FormQuestionTypes, QuestionBlockProps } from './contracts';

/**
 * Function to return what the default answer should be for the given type
 * NOTE: In theory this should work for blocks besides question block but this was developed for Question block so ensure to test thoroughly for first non question block
 * @param type - The question type
 * @param fieldMeta - The blocks fieldMeta, useful in the future to help decide on default answer
 * @returns
 */
export function buildDefaultAnswer(type: FormQuestionTypes | string, fieldMeta: unknown) {
	switch (type) {
		case FormQuestionTypes.Date: {
			return buildDefaultDateAnswer();
		}
		default:
			return undefined;
	}
}

const buildDefaultDateAnswer = () => moment().format(ISO_DATE_FORMAT);

/**
 * Hook to set the default answer for a question based on its type and field metadata.
 * If the form is in preview mode and no answer exists, it will attempt to build a default answer and apply it if the field meta specifies to do so.
 * NOTE: In theory this should work for blocks besides question block but this was developed for Question block so ensure to test thoroughly for first non question block
 * @param id - The block ID.
 * @param questionName - The questionName (question id).
 * @param answerType - The key representing the answer type (e.g., 'answerDate', 'answerString').
 * @returns - This hook doesn't return a value, but it updates the form's answer state when necessary.
 */
export const useDefaultAnswer = (fieldProps: QuestionBlockProps, questionName: string, answerType: keyof IAnswerValue) => {
	const { getAnswers, answerQuestion } = useFormRuntimeContext();
	const answers = getAnswers();
	const fieldMeta = tryParseJSONObject<{ defaultAnswer?: { build?: boolean; shouldReAnswer?: boolean } } & Record<string, unknown>>(
		fieldProps.metadataJson
	);
	const shouldBuildDefault = fieldMeta?.defaultAnswer?.build;
	const shouldReAnswer = fieldMeta?.defaultAnswer?.shouldReAnswer;
	const answer = answers.find(f => f.questionName === questionName)?.answer?.[answerType];

	// TODO - Potentially an initial answers on form answer context or something when we can do that instead of useEffect
	useEffect(() => {
		if (true && (!answer || shouldReAnswer) && shouldBuildDefault) {
			//!design mode brother if (bench.inPreviewMode && (!answer || shouldReAnswer) && shouldBuildDefault) {
			const defaultAnswer = buildDefaultAnswer(fieldProps.type, fieldMeta);
			if (defaultAnswer) {
				answerQuestion({
					answer: {
						[answerType]: defaultAnswer
					},
					questionName: questionName
				});
			}
		}
	}, [answers]);
};
